import React, { useEffect, useState, useCallback } from "react";
import { useLang, LANGUAGES } from "../i18n";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { SectionTitle, Pill, TrustBadge, PrimaryButton, WhyBanner } from "../components/common";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ShieldCheck, ShieldAlert, Loader2, Check, LogOut, Type, Contrast, Globe, FileCheck, Landmark, HelpCircle } from "lucide-react";

export default function Profile() {
  const { t, lang, setLang } = useLang();
  const { user, logout, updateProfile, refresh } = useAuth();
  const [trust, setTrust] = useState(null);
  const [kycOpen, setKycOpen] = useState(false);
  const [idType, setIdType] = useState("aadhaar");
  const [number, setNumber] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [large, setLarge] = useState(localStorage.getItem("gs_large") === "1");
  const [contrast, setContrast] = useState(localStorage.getItem("gs_contrast") === "1");

  const loadTrust = useCallback(() => api.get("/trust/me").then((r) => setTrust(r.data)), []);
  useEffect(() => { loadTrust(); }, [user, loadTrust]);

  const sendOtp = async () => { setBusy(true); try { await api.post("/kyc/send-otp", { id_type: idType, number }); setOtpSent(true); } finally { setBusy(false); } };
  const verify = async () => {
    setBusy(true);
    try { await api.post("/kyc/verify-otp", { id_type: idType, number, otp }); setKycOpen(false); setOtpSent(false); setNumber(""); setOtp(""); await Promise.all([refresh(), loadTrust()]); }
    catch (e) { alert(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const filePolice = async () => { await api.post("/kyc/police-file"); await loadTrust(); alert("Police verification submitted."); };

  const toggleLarge = (v) => { setLarge(v); localStorage.setItem("gs_large", v ? "1" : "0"); document.documentElement.classList.toggle("gs-large", v); };
  const toggleContrast = (v) => { setContrast(v); localStorage.setItem("gs_contrast", v ? "1" : "0"); document.documentElement.classList.toggle("gs-contrast", v); };

  const kyc = user?.kyc || {};

  return (
    <div className="space-y-5">
      <div className="gs-card p-5 flex items-center gap-4 gs-rise">
        <div className="w-16 h-16 rounded-2xl bg-terracotta text-white flex items-center justify-center font-head font-bold text-2xl">{user?.name?.[0]?.toUpperCase()}</div>
        <div className="flex-1">
          <div className="font-head font-bold text-lg text-slate-900">{user?.name}</div>
          <div className="text-xs text-slate-400">{user?.email}</div>
          <div className="mt-1"><Pill tone="terracotta">{user?.role === "owner" ? t("owner") : t("tenant")}</Pill></div>
        </div>
      </div>

      {/* Trust badge */}
      <div className="gs-card p-4" data-testid="trust-badge-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-head font-semibold text-slate-800"><ShieldCheck className="w-5 h-5 text-trust" /> {t("trust_badge")}</div>
          {trust && <TrustBadge trust={trust} />}
        </div>
        {trust && <div className="text-xs text-slate-400 mt-2">Identity verified: {trust.breakdown.identity_verified}/2 · On-time payments: {trust.breakdown.on_time_payments} · Police: {trust.breakdown.police_verification}</div>}
      </div>

      {/* KYC */}
      <div>
        <SectionTitle>{t("verify_identity")}</SectionTitle>
        <WhyBanner>{t("why_kyc")}</WhyBanner>
        <div className="space-y-2.5 mt-3">
          <KycRow icon={FileCheck} label={t("aadhaar")} value={kyc.aadhaar} onStart={() => { setIdType("aadhaar"); setKycOpen(true); }} t={t} testId="kyc-aadhaar-btn" />
          <KycRow icon={FileCheck} label={t("pan")} value={kyc.pan} onStart={() => { setIdType("pan"); setKycOpen(true); }} t={t} testId="kyc-pan-btn" />
          <div className="gs-card p-3.5 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500"><Landmark className="w-5 h-5" /></div>
            <div className="flex-1"><div className="font-semibold text-slate-800">{t("police_verification")}</div><div className="text-xs text-slate-400 capitalize">{kyc.police?.replace("_", " ")}</div></div>
            {kyc.police === "submitted" ? <Pill tone="amber">Under review</Pill> : <button onClick={filePolice} data-testid="police-file-btn" className="text-sm font-semibold text-terracotta">{t("file_police")}</button>}
          </div>
        </div>
      </div>

      {/* Settings */}
      <div>
        <SectionTitle>{t("settings")}</SectionTitle>
        <div className="gs-card divide-y divide-slate-100">
          <div className="p-3.5 flex items-center gap-3"><Globe className="w-5 h-5 text-slate-500" /><span className="flex-1 font-semibold text-slate-700">{t("language")}</span>
            <select value={lang} onChange={(e) => { setLang(e.target.value); updateProfile({ language: e.target.value }); }} data-testid="settings-language" className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm">{LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.native}</option>)}</select>
          </div>
          <div className="p-3.5 flex items-center gap-3"><Type className="w-5 h-5 text-slate-500" /><span className="flex-1 font-semibold text-slate-700">{t("large_text")}</span><Switch checked={large} onCheckedChange={toggleLarge} data-testid="toggle-large-text" /></div>
          <div className="p-3.5 flex items-center gap-3"><Contrast className="w-5 h-5 text-slate-500" /><span className="flex-1 font-semibold text-slate-700">{t("high_contrast")}</span><Switch checked={contrast} onCheckedChange={toggleContrast} data-testid="toggle-contrast" /></div>
        </div>
      </div>

      <div className="flex items-center gap-1.5 justify-center text-xs text-slate-400"><ShieldCheck className="w-3.5 h-3.5 text-trust" /> {t("encrypted_note")}</div>
      <button onClick={logout} data-testid="logout-btn" className="gs-tap w-full rounded-xl border border-red-200 text-red-500 font-semibold flex items-center justify-center gap-2"><LogOut className="w-4 h-4" /> {t("logout")}</button>

      <Dialog open={kycOpen} onOpenChange={setKycOpen}>
        <DialogContent className="max-w-[92vw] sm:max-w-md rounded-2xl">
          <DialogHeader><DialogTitle className="font-head text-left">{idType === "aadhaar" ? t("aadhaar") : t("pan")} · {t("verify")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <WhyBanner>{t("why_kyc")}</WhyBanner>
            <Input className="rounded-xl gs-tap" data-testid="kyc-number-input" placeholder={idType === "aadhaar" ? "XXXX XXXX XXXX" : "ABCDE1234F"} value={number} onChange={(e) => setNumber(e.target.value)} disabled={otpSent} />
            {!otpSent ? <PrimaryButton onClick={sendOtp} disabled={busy || !number} data-testid="kyc-send-otp-btn">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : t("send_otp")}</PrimaryButton> : (
              <>
                <p className="text-xs text-trust">OTP sent (demo OTP: 123456)</p>
                <Input className="rounded-xl gs-tap" data-testid="kyc-otp-input" placeholder={t("enter_otp")} value={otp} onChange={(e) => setOtp(e.target.value)} />
                <PrimaryButton onClick={verify} disabled={busy || !otp} data-testid="kyc-verify-btn">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : t("verify")}</PrimaryButton>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KycRow({ icon: Icon, label, value, onStart, t, testId }) {
  return (
    <div className="gs-card p-3.5 flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500"><Icon className="w-5 h-5" /></div>
      <div className="flex-1"><div className="font-semibold text-slate-800">{label}</div><div className="text-xs text-slate-400">{value ? value.masked : t("not_verified")}</div></div>
      {value?.verified ? <Pill tone="trust"><Check className="w-3 h-3" /> {t("verified")}</Pill> : <button onClick={onStart} data-testid={testId} className="text-sm font-semibold text-terracotta">{t("verify")}</button>}
    </div>
  );
}
